import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Wifi, Zap, Link as LinkIcon, Headphones, Smartphone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import ROYAPOSDevice from '../components/ROYAPOSDevice';

const POSDevices = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Wifi,
      title: t('pos.features.wireless'),
      desc: t('pos.features.wirelessDesc'),
    },
    {
      icon: Zap,
      title: t('pos.features.fast'),
      desc: t('pos.features.fastDesc'),
    },
    {
      icon: LinkIcon,
      title: t('pos.features.integrated'),
      desc: t('pos.features.integratedDesc'),
    },
    {
      icon: Headphones,
      title: t('pos.features.support'),
      desc: t('pos.features.supportDesc'),
    },
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="heading-1 mb-6">{t('pos.title')}</h1>
            <p className="heading-3 text-gray-600 mb-4">{t('pos.subtitle')}</p>
            <p className="text-lg text-gray-600">{t('pos.description')}</p>
          </div>

          {/* POS Device Image */}
          <div className="mt-12 flex justify-center">
            <div 
              className="relative"
              style={{
                maxWidth: '500px',
                transform: 'perspective(1000px) rotateY(-3deg)',
                transition: 'transform 0.4s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'perspective(1000px) rotateY(0deg) scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'perspective(1000px) rotateY(-3deg)'}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/vqislnlt_WhatsApp%20Image%201447-05-27%20at%2007.25.17.jpeg"
                alt="ROYA POS Device"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '24px',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3), 0 0 100px rgba(27, 158, 75, 0.2)',
                }}
              />
              
              {/* Decorative elements */}
              <div
                style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '100px',
                  height: '100px',
                  background: 'radial-gradient(circle, rgba(27, 158, 75, 0.3) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-20px',
                  left: '-20px',
                  width: '120px',
                  height: '120px',
                  background: 'radial-gradient(circle, rgba(245, 194, 66, 0.3) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Android Phone as POS Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <div
                  className="relative rounded-3xl overflow-hidden"
                  style={{
                    boxShadow: '0 25px 50px rgba(27, 158, 75, 0.3)',
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/zxtuhfw0_WhatsApp%20Image%201447-05-27%20at%2007.52.45.jpeg"
                    alt="Android Phone as POS"
                    className="w-full h-auto"
                  />
                </div>

                {/* Decorative elements */}
                <div
                  className="absolute -top-6 -left-6 w-32 h-32 bg-green-400 rounded-full opacity-20 blur-3xl"
                />
                <div
                  className="absolute -bottom-6 -right-6 w-32 h-32 bg-yellow-400 rounded-full opacity-20 blur-3xl"
                />
              </div>

              {/* Text Content */}
              <div>
                <div className="inline-block px-4 py-2 bg-green-100 rounded-full text-green-700 font-medium text-sm mb-6">
                  Smartphone POS
                </div>
                <h2 className="heading-2 mb-6">Turn Your Android Phone into a POS</h2>
                <p className="text-xl font-semibold mb-4" style={{ color: '#1B9E4B' }}>
                  Transform your Android phone into a secure POS terminal — accept mada, Visa, and Mastercard payments instantly with ROYA
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  No need for expensive hardware. Download the ROYA app, and start accepting contactless payments with your existing Android smartphone. Perfect for small businesses, delivery services, and on-the-go merchants.
                </p>

                <div className="space-y-4 mb-8">
                  {[
                    { 
                      title: 'Zero Hardware Cost', 
                      desc: 'Use your existing Android phone — no additional device needed' 
                    },
                    { 
                      title: 'NFC Contactless Payments', 
                      desc: 'Accept tap-to-pay transactions with mada, Visa, and Mastercard' 
                    },
                    { 
                      title: 'Instant Setup', 
                      desc: 'Download the app, register, and start accepting payments in minutes' 
                    },
                    { 
                      title: 'Secure & Certified', 
                      desc: 'Bank-grade security with PCI-DSS compliance' 
                    },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                        style={{ background: 'linear-gradient(135deg, #1B9E4B 0%, #159642 100%)' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">{feature.title}</h4>
                        <p className="text-sm text-gray-600">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/contact">
                  <Button className="btn-primary">
                    <Smartphone className="h-5 w-5" />
                    Start Using Your Phone as POS
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 text-center mb-12">Device Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="product-card">
                <CardContent className="p-6 text-center">
                  <div
                    className="inline-flex p-4 rounded-full mb-4"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
                    }}
                  >
                    <feature.icon className="h-8 w-8" style={{ color: '#10B981' }} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 text-center mb-12">Why Choose ROYA POS?</h2>

            <div className="space-y-6">
              {[
                'Modern Android-based system with intuitive interface',
                'Wireless connectivity for maximum flexibility',
                'Fast activation - start accepting payments in minutes',
                'Direct integration with ROYA wallet ecosystem',
                'Support for all major payment methods (mada, Visa, Mastercard)',
                'Real-time transaction notifications',
                '24/7 technical support',
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </div>
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/contact">
                <Button className="btn-primary text-lg px-8 py-6">Request a Demo</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default POSDevices;
